import { useState, useEffect } from 'react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { HotspotObject, Hotspot, PhotoMarker } from '../types/types';
import {
  getLayoutImageWithSingleMarker,
  getLayoutImageZoomedOnMarker,
} from '../utils/getLayoutImageWithSingleMarker';
import { toast } from '@/shared/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { GET, POST, handleApiError } from '@/shared/api/requests';

export interface SharePhotoDialogProps {
  open: boolean;
  onClose: () => void;
  object: HotspotObject;
  objects?: HotspotObject[];
  overlayGroupId?: number | null;
  hotspot: Hotspot;
  markerNumber?: number;
  layoutImageUrl?: string;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
  projectId?: string;
}

const TYPE_OPTIONS = [
  { value: 'RFI', label: 'RFI' },
  { value: 'Other', label: 'Other' },
];

const TRADE_OPTIONS = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'plumbing', label: 'Plumbing' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

type AssignedToOption = {
  value: string;
  label: string;
};

type ShareableUser = {
  id: number | string;
  email?: string;
  first_name?: string;
  last_name?: string;
};

const SharePhotoDialog: React.FC<SharePhotoDialogProps> = ({
  open,
  onClose,
  object,
  objects,
  overlayGroupId,
  hotspot,
  markerNumber,
  layoutImageUrl,
  layoutImageDimensions,
  photoMarkers,
  projectId,
}) => {
  const [type, setType] = useState<string>('RFI');
  const [trade, setTrade] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [priority, setPriority] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [specSection, setSpecSection] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageObjects =
    objects && objects.length > 0 ? objects.filter((item) => item.type === 'image') : [object];
  const imageUrl = (object.rawData as { url?: string })?.url;
  const marker = photoMarkers?.find((m) => m.number === markerNumber);
  const layoutWidth = layoutImageDimensions?.width ?? 0;
  const layoutHeight = layoutImageDimensions?.height ?? 0;

  const canRenderLocationPreviews = Boolean(layoutImageUrl && marker && layoutWidth > 0 && layoutHeight > 0);

  // 30% crop window for zoomed view, clamped to edges to avoid whitespace.
  const cropFraction = 0.3;
  const cropWidth = Math.max(1, layoutWidth * cropFraction);
  const cropHeight = Math.max(1, layoutHeight * cropFraction);
  const cropLeft =
    marker != null ? Math.max(0, Math.min(marker.x - cropWidth / 2, layoutWidth - cropWidth)) : 0;
  const cropTop =
    marker != null ? Math.max(0, Math.min(marker.y - cropHeight / 2, layoutHeight - cropHeight)) : 0;

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setType('RFI');
      setTrade('');
      setDueDate('');
      setPriority('');
      setAssignedTo('');
      setSpecSection('');
      setDescription('');
    }
  }, [open]);

  const shareableUsersQuery = useQuery({
    queryKey: ['shareableUsers', projectId],
    queryFn: async () => {
      const response = await GET(`/base/projects/${projectId}/shareable-users/`);
      return response.data;
    },
    enabled: open && Boolean(projectId),
    staleTime: 1000 * 60 * 5,
  });

  const assignedToOptions: AssignedToOption[] = useMemo(() => {
    return (shareableUsersQuery.data?.users || []).map((user: ShareableUser) => {
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const label = fullName || user.email || `User ${user.id}`;
      return {
        value: String(user.id),
        label,
      };
    });
  }, [shareableUsersQuery.data]);

  const handleShare = async () => {
    setIsSubmitting(true);

    try {
      const shareMarker = photoMarkers?.find((m) => m.number === markerNumber);
      let layoutBlob: Blob | null = null;
      let layoutZoomedBlob: Blob | null = null;
      const buildHotspotDeepLink = () => {
        if (typeof window === 'undefined') return '';

        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);

        if (hotspot.photo_id) {
          params.set('photo', String(hotspot.photo_id));
        }
        params.set('hotspot', String(hotspot.id));
        params.set('pitch', String(hotspot.pitch));
        params.set('yaw', String(hotspot.yaw));

        const query = params.toString();
        return `${currentUrl.origin}${currentUrl.pathname}${query ? `?${query}` : ''}`;
      };

      // Keep share flow resilient: if blob generation fails, still log payload.
      if (layoutImageUrl && shareMarker && layoutImageDimensions) {
        try {
          layoutBlob = await getLayoutImageWithSingleMarker(
            layoutImageUrl,
            shareMarker,
            layoutImageDimensions.width,
            layoutImageDimensions.height
          );
          layoutZoomedBlob = await getLayoutImageZoomedOnMarker(
            layoutImageUrl,
            shareMarker,
            layoutImageDimensions.width,
            layoutImageDimensions.height,
            300
          );
        } catch (err) {
          console.warn('Layout blob generation failed; continuing without it.', err);
        }
      }

      const payload = {
        type,
        trade,
        due_date: dueDate,
        priority,
        assigned_to: assignedTo,
        assigned_to_name: assignedToOptions.find((opt) => opt.value === assignedTo)?.label || '',
        spec_section: specSection,
        location: markerNumber != null ? `Marker ${markerNumber}` : 'N/A',
        description,
        photo_url: imageUrl,
        project_id: projectId,
        hotspot_id: hotspot.id,
        object_title: object.title,
        hotspot_deep_link_url: buildHotspotDeepLink(),
        layout_image_blob: layoutBlob,
        layout_image_size: layoutBlob?.size,
      };

      // Submit to backend API as multipart to support layout preview uploads.
      try {
        const shareData = new FormData();
        shareData.append('hotspot_id', String(hotspot.id));
        shareData.append('shared_to', assignedTo);
        if (projectId) {
          shareData.append('project_id', projectId);
        }
        shareData.append('type', type);
        shareData.append('trade', trade);
        if (dueDate) {
          shareData.append('due_date', dueDate);
        }
        shareData.append('priority', priority);
        shareData.append('spec_section', specSection);
        shareData.append('location', payload.location);
        shareData.append('description', description);
        if (overlayGroupId) {
          shareData.append('overlay_group_id', String(overlayGroupId));
        }

        if (layoutBlob) {
          shareData.append('layout_image_blob', layoutBlob, 'layout-preview.png');
        }
        if (layoutZoomedBlob) {
          shareData.append('layout_zoomed_image_blob', layoutZoomedBlob, 'layout-zoomed-preview.png');
        }
        shareData.append('photo_count', String(imageObjects.length));
        if (payload.object_title) {
          shareData.append('object_title', payload.object_title);
        }
        if (payload.hotspot_deep_link_url) {
          shareData.append('hotspot_deep_link_url', payload.hotspot_deep_link_url);
        }

        await POST('/hotspots/share/', shareData);

        toast({
          title: imageObjects.length > 1 ? 'Photo group shared successfully' : 'Photo shared successfully',
          description: `Shared with ${payload.assigned_to_name}.`,
          variant: 'default',
        });

        onClose();
      } catch (error) {
        console.error('Share error:', error);
        const errorMessage = handleApiError(error);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Share error:', error);
      toast({
        title: 'Error',
        description: 'Failed to prepare share. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Photo</DialogTitle>
          <DialogDescription className="sr-only">
            Fill in the form to share this hotspot photo via email or text.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{imageObjects.length > 1 ? `Photos (${imageObjects.length})` : 'Photo'}</Label>
            {imageObjects.length > 1 ? (
              <div className="rounded-lg border p-3 bg-muted/50">
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {imageObjects.map((imageObject) => {
                    const url = (imageObject.rawData as { url?: string })?.url;
                    return (
                      <div
                        key={`share-thumb-${imageObject.id}`}
                        className="aspect-square rounded-md overflow-hidden border bg-card"
                        title={imageObject.title || 'Hotspot photo'}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={imageObject.title || 'Shared hotspot photo'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                            N/A
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : imageUrl ? (
              <div className="rounded-lg border overflow-hidden bg-muted/50">
                <img
                  src={imageUrl}
                  alt={object.title || 'Shared hotspot photo'}
                  className="w-full object-contain"
                  style={{ maxHeight: '250px', height: 'auto', display: 'block', margin: '0 auto' }}
                />
              </div>
            ) : (
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                No photo available
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-type">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="share-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-trade">Trade</Label>
            <Select value={trade} onValueChange={setTrade}>
              <SelectTrigger id="share-trade">
                <SelectValue placeholder="Select trade" />
              </SelectTrigger>
              <SelectContent>
                {TRADE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-due-date">Due Date</Label>
            <Input
              id="share-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="share-priority">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              {markerNumber != null ? `Marker ${markerNumber}` : 'N/A'}
            </div>
            {canRenderLocationPreviews && marker && layoutImageUrl && (
              <div className="mt-2 flex gap-2 min-h-[8rem]">
                <div className="flex-1 rounded-lg border overflow-hidden bg-muted/50 min-w-0 flex flex-col">
                  <span className="text-xs text-muted-foreground px-2 py-1">Full layout</span>
                  <svg
                    className="w-full h-32"
                    viewBox={`0 0 ${layoutWidth} ${layoutHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <image href={layoutImageUrl} x="0" y="0" width={layoutWidth} height={layoutHeight} />
                    <rect
                      x={marker.x - marker.width / 2}
                      y={marker.y - marker.height / 2}
                      width={marker.width}
                      height={marker.height}
                      fill="#4477ff"
                      stroke="black"
                      strokeWidth="1"
                    />
                    <text
                      x={marker.x}
                      y={marker.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize={marker.fontSize}
                      fontWeight="600"
                    >
                      {marker.number}
                    </text>
                  </svg>
                </div>

                <div className="flex-1 rounded-lg border overflow-hidden bg-muted/50 min-w-0 flex flex-col">
                  <span className="text-xs text-muted-foreground px-2 py-1">Zoomed</span>
                  <svg
                    className="w-full h-32"
                    viewBox={`${cropLeft} ${cropTop} ${cropWidth} ${cropHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <image href={layoutImageUrl} x="0" y="0" width={layoutWidth} height={layoutHeight} />
                    <rect
                      x={marker.x - marker.width / 2}
                      y={marker.y - marker.height / 2}
                      width={marker.width}
                      height={marker.height}
                      fill="#4477ff"
                      stroke="black"
                      strokeWidth="1"
                    />
                    <text
                      x={marker.x}
                      y={marker.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize={marker.fontSize}
                      fontWeight="600"
                    >
                      {marker.number}
                    </text>
                  </svg>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-assigned-to">Assigned to</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger id="share-assigned-to">
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {shareableUsersQuery.isLoading ? (
                  <SelectItem value="__loading" disabled>
                    Loading team members...
                  </SelectItem>
                ) : assignedToOptions.length > 0 ? (
                  assignedToOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="__empty" disabled>
                    No team members found
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-spec-section">Spec section</Label>
            <Input
              id="share-spec-section"
              value={specSection}
              onChange={(e) => setSpecSection(e.target.value)}
              placeholder="Enter spec section..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="share-description">Description</Label>
            <Textarea
              id="share-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleShare} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              'Share'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SharePhotoDialog;
